/**
 * pickMediaComponent — single source of truth for the RAG media switch.
 *
 * Both the composed path (rag-answer-block) and the registry-fallback path
 * (rag-content) route through this so a given result never renders differently
 * between them. Order matters: an all-shorts set is claimed FIRST (its own
 * vertical Shelf), before the generic 1-vs-many split.
 *
 *   every ref is a /shorts/ → ShortsShelf   (9:16 vertical, own player)
 *   exactly one ref         → MediaHeroCard (16:9 featured hero)
 *   several refs            → RelatedMediaPanel (master-detail, mixed media)
 *
 * Callers keep their own FadeSlideIn / wrapper behavior around the result.
 */

import React from 'react';
import type { RAGReference } from '@/types/rag';
import { MediaHeroCard } from './media-hero-card';
import { RelatedMediaPanel } from './related-media-panel';
import { ShortsShelf } from './shorts-shelf';

const isShortUrl = (url: string | null | undefined): boolean =>
  /\/shorts\//.test(url || '');

export function pickMediaComponent(
  sorted: RAGReference[]
): React.ReactElement | null {
  if (sorted.length === 0) return null;
  if (sorted.every((r) => isShortUrl(r.url))) {
    return <ShortsShelf references={sorted} />;
  }
  if (sorted.length === 1) {
    return <MediaHeroCard refItem={sorted[0]} />;
  }
  return <RelatedMediaPanel references={sorted} />;
}
